/**
 * Dependency-free donut chart (inline SVG). A thin ring split into segments,
 * each shortened by a 2px gap so the card surface shows through and marks stay
 * separable WITHOUT relying on color alone. The legend carries direct value
 * labels + percentages (the required secondary encoding); the whole figure has
 * an accessible <title>. Colors come from charts.css CSS variables.
 */

import type { CSSProperties } from "react";

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  /** A CSS color, e.g. "var(--chart-live)". */
  color: string;
}

export interface DonutProps {
  segments: DonutSegment[];
  /** Accessible name for the whole figure. */
  title: string;
  /** Center caption under the total (e.g. "passports"). */
  caption: string;
  /** Format a count for display (locale-aware). */
  formatNumber: (n: number) => string;
}

const SIZE = 132;
const STROKE = 16;
const R = (SIZE - STROKE) / 2;
const C = 2 * Math.PI * R;
const CENTER = SIZE / 2;
const GAP = 2; // px of surface between segments

export function Donut({ segments, title, caption, formatNumber }: DonutProps) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const nonZero = segments.filter((s) => s.value > 0);

  let offset = 0;
  const arcs = nonZero.map((seg, i) => {
    const frac = seg.value / total;
    const arcLen = frac * C;
    // Shorten each arc by the gap (min so tiny slices never invert).
    const dash = Math.max(arcLen - GAP, 0.001);
    const node = (
      <circle
        key={seg.key}
        className="dpp-donut__arc"
        cx={CENTER}
        cy={CENTER}
        r={R}
        fill="none"
        stroke={seg.color}
        strokeWidth={STROKE}
        strokeDasharray={`${dash} ${C - dash}`}
        strokeDashoffset={-offset}
        // Feeds the draw-in keyframe in charts.css; the attributes above stay the
        // rendered truth once it has played (and when motion is reduced).
        style={{ "--dash": dash, "--rest": C - dash, "--circ": C, "--i": i } as CSSProperties}
      />
    );
    offset += arcLen;
    return node;
  });

  return (
    <div className="dpp-donut">
      <div className="dpp-donut__figure">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} role="img" aria-label={title}>
          <title>{title}</title>
          {/* Track: the empty ring when there is no data (or behind gaps). */}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={R}
            fill="none"
            stroke="var(--chart-grid)"
            strokeWidth={total === 0 ? STROKE : 0}
          />
          {/* Rotate -90° so segments start at 12 o'clock, clockwise. */}
          <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>{arcs}</g>
        </svg>
        <div className="dpp-donut__center">
          <span className="dpp-donut__total">{formatNumber(total)}</span>
          <span className="dpp-donut__caption">{caption}</span>
        </div>
      </div>

      <ul className="dpp-legend" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {segments.map((seg) => {
          const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
          return (
            <li key={seg.key} className="dpp-legend__row">
              <span className="dpp-legend__dot" style={{ background: seg.color }} aria-hidden="true" />
              <span className="dpp-legend__label">{seg.label}</span>
              <span className="dpp-legend__count">{formatNumber(seg.value)}</span>
              <span className="dpp-legend__pct">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
